import { faSquareArrowDownRight, faSquareDashed } from '@fortawesome/pro-solid-svg-icons';
import { faArrowDown } from "@fortawesome/pro-solid-svg-icons/faArrowDown";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Breadcrumbs, Button, Chip, Grid, Link, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT, DRAWER_MAX_SELECTION_COUNT, KEYBOARD_ARROW_DOWN } from 'app/appConstants';
import { MprcIconFa, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { MprcFilterEmptyDataset } from 'components/filter/MprcFilterEmptyDataset';
import { IconButtonClearSearch } from 'components/iconButtons/IconButtonClearSearch';
import { TextFieldFilter } from "components/MprcAdvFilter";
import { MprcBoxDrawer } from 'components/MprcBoxDrawer';
import { MprcContainer } from 'components/MprcContainer';
import { TopicSelectorHierarchicalResponse, TopicSelectorHierarchicalResponsePaginatedList, TopicSelectorMode, TopicSelectorType, UserSelectionEntityType, UserSelectionEntry } from 'generated/CoreApi_v1';
import { TFunction } from 'i18next';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

const SelectionHistoryMessage: React.FC = () => {
    const { t } = useTranslation("shared");
    return <Typography component="div" variant="caption" sx={{ mt: 1 }}>
        <span>{t("Messages.ShowingHistoricMessage")}. <FontAwesomeIcon icon={faArrowDown} /> {t("Messages.ShowingHistoricArrowMessage")}.</span>
    </Typography>;
};

////////////////////////////////////////////////////////////////////////////////

interface TopicResult {
    topicId: string;
    name: string;
    requiredGroupId?: string;
    optionalGroupId?: string;
}

interface IProps {
    title?: string;
    subtitle?: string;
    trySelectionHistory?: boolean;
    mode: TopicSelectorMode;
    multi?: boolean;
    onClose: (topic: TopicResult[] | null) => void;
}

////////////////////////////////////////////////////////////////////////////////

function getDefaultBreadcrumbs(t: TFunction) {

    const ALL_TOPICS = {
        topicId: "ALLTOPICS",
        name: t("Topic.TopicSelectorDrawer.AllTopicsText")
    } as TopicSelectorHierarchicalResponse;

    const SEARCH_RESULTS = {
        topicId: "SEARCHIDRESULT",
        name: t("Topic.TopicSelectorDrawer.SearchResultsText")
    } as TopicSelectorHierarchicalResponse;

    return [ALL_TOPICS, SEARCH_RESULTS];
}

////////////////////////////////////////////////////////////////////////////////

export const TopicSelectorHierarchicalDrawer: React.FC<IProps> = ({ title, subtitle, multi = false, onClose, trySelectionHistory = false, ...props }) => {

    const session = useSession();
    const { t } = useTranslation();
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [ALL_TOPICS, SEARCH_RESULTS] = getDefaultBreadcrumbs(t);

    const [searchText, setSearchText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<TopicSelectorHierarchicalResponsePaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [selectedNode, setSelectedNode] = useState<string | undefined>();
    const [breadcrumbs, setBreadCrumbs] = useState<TopicSelectorHierarchicalResponse[]>([ALL_TOPICS]);
    const [ignoreSelectionHistory, setIgnoreSelectionHistory] = useState<boolean>(trySelectionHistory === true);
    const [selected, setSelection] = useState<TopicSelectorHierarchicalResponse[]>([]);

    const [saveSelection, setSaveSelection] = useState<TopicSelectorHierarchicalResponse[] | undefined>(undefined);

    const searchTextRef = useRef<HTMLInputElement>(null);
    const pageNumber = useRef<number>(1);

    const isSearching = breadcrumbs.findIndex(x => x.topicId === SEARCH_RESULTS.topicId) != -1;
    const resultFromHistory = !ignoreSelectionHistory && viewData != undefined && viewData.totalRows > 0 && viewData.items[0].fromHistory == true;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const initSearch = (clearAll: boolean = false) => {

        if (clearAll === true || searchText == undefined || searchText.length == 0) {
            if (searchText != undefined) setSearchText("");
            setBreadCrumbs([ALL_TOPICS]);
            setSelectedNode(undefined);
        }
        else {
            setBreadCrumbs([SEARCH_RESULTS]);
            setSelectedNode(SEARCH_RESULTS.topicId);
        }

        pageNumber.current = 1;
        setLoading(true);
    };

    ///
    const handleExpandNode = (item: TopicSelectorHierarchicalResponse) => {

        //Se o item selecionado, for diferente do nó atual, altera o breadcrumb
        if (isSearching === false) {
            if (item.topicId !== selectedNode) {

                const currentNodeIndex = breadcrumbs.findIndex(x => x.topicId == item.topicId);
                if (currentNodeIndex === -1) {
                    //Se não existe no breadcrumb, adiciona
                    setBreadCrumbs([...breadcrumbs, item]);
                }
                else {
                    //Se exite no breadcrubs, limpa o nó encontrado e tudo que vem depois
                    const newBreadCrumbs = breadcrumbs.filter((x, i) => i <= currentNodeIndex);
                    setBreadCrumbs([...newBreadCrumbs]);
                }
            }
        } else {
            if (item.topicId == SEARCH_RESULTS.topicId) {
                setBreadCrumbs([SEARCH_RESULTS]);
            } else {
                getBreadcrumbs(item.topicId);
            }
        }

        setIgnoreSelectionHistory(true);
        setSelectedNode(item.topicId);
        setLoading(true);
    };

    ///
    const handleSelect = (item: TopicSelectorHierarchicalResponse) => {
        if (!multi)
            setSaveSelection([item]);
        else {
            if (selected.find(p => p.topicId == item.topicId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("shared:Selector.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("shared:Selector.AlreadySelectedMessage", { name: item.name }), { variant: "error" });
            }
        }
    };

    ///
    const getBreadcrumbs = (topicId: string) => {
        api.v1.selectors.topicsHierarchical({
            pageNumber: 1,
            pageSize: 1000,
            topicId: topicId,
            selectorType: TopicSelectorType.Breadcrumbs,
            trySelectionHistory: false,
            parentId: null,
            searchText: null,
            mode: props.mode
        }).then(result => {
            setBreadCrumbs([SEARCH_RESULTS, ...result.data.items]);
        }).catch(e => setViewError(e));
    };

    /// Salva itens selecionados.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.filter(p => !p.fromHistory).map((v) => ({
                    entityType: UserSelectionEntityType.Topic,
                    entityId: v.topicId,
                    otherInfo: "false"
                } as UserSelectionEntry))
            };

            if (payload.entries.length > 0) {
                api.v1.user.saveSelectionHistory(payload)
                    .then(() => {
                        onClose(saveSelection as TopicResult[]);
                    })
                    .catch((e) => {
                        setSaveSelection(undefined);
                    });
            }
            else {
                onClose(saveSelection as TopicResult[]);
            }
        }
    }, [saveSelection]);

    useEffect(() => {
        async function getData() {

            try {
                var result = await api.v1.selectors.topicsHierarchical({
                    pageNumber: pageNumber.current,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    searchText: selectedNode != SEARCH_RESULTS.topicId ? null : searchText,
                    parentId: selectedNode == SEARCH_RESULTS.topicId || selectedNode == ALL_TOPICS.topicId ? null : selectedNode ?? null,
                    selectorType: TopicSelectorType.Hierarchical,
                    trySelectionHistory: false, //Recurso desabilitado
                    topicId: null,
                    mode: props.mode
                });
                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);

                setTimeout(() => {
                    searchTextRef?.current?.focus();
                }, 600);
            }
        }

        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {viewError && <MprcRequestErrorReport error={viewError} />}
            <MprcBoxDrawer
                open
                onClose={() => onClose(null)}
                title={title || t("Topic.TopicSelectorDrawer.Title")}
                subtitle={subtitle}
                header={
                    <>
                        <TextFieldFilter
                            autoFocus
                            fullWidth
                            autoComplete="off"
                            placeholder={t("Topic.TopicSelectorDrawer.SearchTextPlaceholder")}
                            value={searchText}
                            disabled={loading}
                            onChange={() => {
                                setSearchText(searchTextRef.current?.value ?? "");
                            }}
                            onKeyDown={(e) => {
                                if (e.keyCode == 13) initSearch();
                                else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) {
                                    initSearch();
                                    setIgnoreSelectionHistory(true);
                                }
                            }}
                            inputRef={searchTextRef}
                            slotProps={{
                                input: {
                                    endAdornment:
                                        searchText != "" &&
                                        <IconButtonClearSearch
                                            size="small"
                                            onClick={() => initSearch(true)}
                                        />
                                }
                            }}
                        />
                        {resultFromHistory && <SelectionHistoryMessage />}
                    </>
                }
                content={
                    <MprcContainer>
                        {loading ?
                            <MprcSpinnerIndicator /> :
                            <>
                                {!resultFromHistory && breadcrumbs.length > 0 &&
                                    <MprcContainer>
                                        <Breadcrumbs aria-label="breadcrumb" color="primary">
                                            {
                                                breadcrumbs.map((item) =>
                                                    <Link
                                                        key={item.topicId}
                                                        underline="hover"
                                                        color="inherit"
                                                        fontSize="12px"
                                                        href="#"
                                                        onClick={() => handleExpandNode(item)}
                                                    >
                                                        {item.name}
                                                    </Link>
                                                )
                                            }
                                        </Breadcrumbs>
                                    </MprcContainer>
                                }
                                {
                                    (viewData?.totalRows ?? 0) === 0
                                        ? <MprcFilterEmptyDataset message={t("Topic.TopicSelectorDrawer.NoRecordsFoundText")} />
                                        : <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                            {
                                                viewData?.items.map((item, index) => {
                                                    return (
                                                        <Grid size={{ xs: 12 }} key={index}>

                                                            <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1}>
                                                                <Grid>
                                                                    <MprcLink
                                                                        onClick={item.hasChildren ? () => handleExpandNode(item) : undefined}
                                                                    >
                                                                        <MprcIconFa
                                                                            icon={item.hasChildren ? faSquareArrowDownRight : faSquareDashed}
                                                                            color="muted"
                                                                            size="lg"
                                                                            fixedWidth
                                                                        />
                                                                    </MprcLink>
                                                                </Grid>
                                                                <Grid size="grow">
                                                                    <MprcLink
                                                                        onClick={item.selectable ? () => handleSelect(item) : undefined}
                                                                    >
                                                                        <Typography variant="h5">
                                                                            {item.name}
                                                                        </Typography>
                                                                    </MprcLink>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })
                                            }
                                        </Grid>
                                }
                            </>
                        }
                    </MprcContainer>
                }
                footer={
                    <div style={{ width: "100%" }}>
                        {
                            multi && selected.length > 0 &&
                            <>
                                <div style={{ maxHeight: 200, overflow: "auto" }}>
                                    <p><small>{t("shared:Selector.SelectedText")}:</small></p>
                                    {
                                        selected.map((item, index) => (
                                            <Chip
                                                key={index}
                                                label={item.name}
                                                size="small"
                                                sx={{ mb: 0.5, mr: 0.5 }}
                                                avatar={<Avatar src={item.thumbnailUrl} />}
                                                onDelete={() => setSelection(selected.filter(p => p.topicId != item.topicId))}
                                            />
                                        ))
                                    }
                                </div>
                                <hr />
                                <div style={{ textAlign: "center" }}>
                                    <Button
                                        variant="contained"
                                        disabled={saveSelection != undefined}
                                        endIcon={saveSelection == undefined ? undefined : <MprcSpinnerIndicator />}
                                        onClick={() => setSaveSelection(selected)}
                                    >
                                        {t("shared:Selector.AddSelectedButtonText")}
                                    </Button >
                                </div>
                            </>
                        }
                    </div>
                }
                requestIndicator={
                    saveSelection != undefined ? "post" : undefined
                }
            />
        </>
    );
};
